import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ID } from '@datorama/akita';
import { EMPTY } from 'rxjs';
import { distinctUntilChanged, expand, reduce, scan } from 'rxjs/operators';
import { Resource } from '../model/resource';
import { NetworkService } from './network.service';

const limitVariable = 'limit';

export abstract class GenericAkitaHTTPService<T extends Resource> {

    private entitiesUrl = '';
    updateUrl: string;

    constructor(
        protected http: HttpClient,
        protected networkService: NetworkService,
        protected url: string,
        protected type: string,
        protected options?: HttpOptions
    ) { }

    setEntitiesUrl(newHost: string) {
        this.entitiesUrl = newHost + '/v2/entities/';
        this.updateUrl =  newHost + "/v2/op/update";
    }
    /**
     * Gets all data using pagination, but emits everytime a new batch of data has been received
     */
    getAll(options?: HttpOptions) {

        const optionsToUse = options || this.options;

        const limit = Number(optionsToUse?.params[limitVariable]) || 100;
        let offset = 0;
        return this.getNextXItems(offset, limit, optionsToUse).pipe(
            expand(data => data.length > 0 ? this.getNextXItems(offset += limit, limit, optionsToUse) : EMPTY),
            distinctUntilChanged(),
            scan((acc: T[], x) => acc.concat(x), []));
    }

    getMaxItems(maxEntries: number, options?: HttpOptions) {
        const optionsToUse = options || this.options;

        const limit = maxEntries;
        let offset = 0;
        return this.getNextXItems(offset, limit, optionsToUse).pipe(
            expand(data => data.length > 0 && data.length <= maxEntries - limit ?
                this.getNextXItems(offset += limit, limit, optionsToUse) : EMPTY),
            distinctUntilChanged(),
            scan((acc: T[], x) => acc.concat(x), []));
    }

    /**
     *
     *  Gets all data using pagination, but emits only if all data loading is complete
     */
    getAllWithoutScan(options?: HttpOptions) {
        const optionsToUse = options || this.options;

        const limit = Number(optionsToUse?.params[limitVariable]) || 100;
        let offset = 0;
        return this.getNextXItems(offset, limit, optionsToUse).pipe(
            expand(data => data.length > 0 ? this.getNextXItems(offset += limit, limit, optionsToUse) : EMPTY),
            distinctUntilChanged(),
            reduce((acc: T[], x) => acc.concat(x), []));
    }


    getNextXItems(offset: number, limit: number, options?: HttpOptions) {

        options = options || this.options;
        const paginationOptions = options;
        paginationOptions.params = {
            ...options.params,
            offset: offset.toString(),
            limit: limit.toString()
        };
        return this.http.get<T[]>(this.entitiesUrl, paginationOptions);
    }


    loadById(id: ID, options?: HttpOptions) {
        const url = this.entitiesUrl + id;
        options = options || this.options;

        return this.http.get<T>(url, options);
    }

    executeQuery(query: string, type: string, keyValues?: boolean, limit = 100) {
        let options: HttpOptions = {
            params: {
                q: query,
                type,
            }
        };

        if (keyValues) {
            options = {
                params: {
                    q: query,
                    type,
                    options: 'keyValues'
                }
            };
        }

        let offset = 0;
        return this.getNextXItems(offset, limit, options).pipe(
            expand(data => data.length > 0 ? this.getNextXItems(offset += limit, limit, options) : EMPTY),
            distinctUntilChanged(),
            reduce((acc: T[], x) => acc.concat(x), []));
    }

    add(entity: T, options: HttpOptions) {
        const tempOptions = { ...this.options, options };
        return this.http.post<T>(this.entitiesUrl, entity, tempOptions);
    }

    update(entity: Partial<T | T[]>, actionType: UpdateActionType, options: HttpOptions) {
        const body = {
            actionType,
            entities: Array.isArray(entity) ? entity : [entity]
        };
        const tempOptions = { ...this.options, options };
        return this.http.post<T>(this.updateUrl, body, tempOptions);
    }

}
export interface HttpOptions {
    headers?: HttpHeaders | {
        [header: string]: string | string[];
    };
    params?: HttpParams | {
        [param: string]: string | string[];
    };
}
/**
 *  Action types for POST /v2/op/Update
 *
 *  https://fiware-orion.readthedocs.io/en/master/user/update_action_types/index.html
 */
export enum UpdateActionType {
    APPEND = 'append',
    APPEND_STRUICT = 'appendStrict',
    UPDATE = 'update',
    DELETE = 'delete',
    REPLACE = 'replace'
}
